/**
 * Built-in word sets. The initial-/ɹ/ set mirrors the spec appendix; the
 * default-lanes set binds targets to the shipped `hiss/airy/pop/hum`
 * clusters so posterior alignment can be exercised without a speech library.
 * Vowels are deliberately left without a clusterId (provisional phones).
 */
export const INITIAL_R_WORD_SET = {
    schemaVersion: 1,
    id: "en-us.initial-r",
    title: "Initial /ɹ/ — single words",
    description: "Word-initial rhotics with easy scaffolding codas.",
    locale: "en-US",
    license: "CC-BY-4.0",
    tags: ["articulation", "rhotics", "word-initial"],
    phones: [
        {
            id: "r",
            ipa: "ɹ",
            display: "/r/",
            kind: "sustained",
            pitchInvariant: true,
            minHoldMs: 160,
            coaching: "Curl the tongue back; don't round to a /w/.",
            commonSubstitutions: ["w", "l"],
            calibration: {
                expect: "uncalibrated",
                provision: true,
                counterLabels: ["w", "l"],
                bootstrap: { minSamples: 12, minDistinctSessions: 2, promoteAt: 40 },
            },
        },
        { id: "w", ipa: "w", display: "w", kind: "sustained", minHoldMs: 80, counterFor: ["r"] },
        { id: "l", ipa: "l", display: "l", kind: "sustained", minHoldMs: 80, counterFor: ["r"] },
        { id: "ae", ipa: "æ", display: "a", kind: "sustained", minHoldMs: 90 },
        { id: "ey", ipa: "eɪ", display: "ai", kind: "sustained", minHoldMs: 110 },
        { id: "eh", ipa: "ɛ", display: "e", kind: "sustained", minHoldMs: 80 },
        { id: "ih", ipa: "ɪ", display: "i", kind: "sustained", minHoldMs: 60 },
        { id: "b", ipa: "b", display: "b", kind: "percussive" },
        { id: "t", ipa: "t", display: "t", kind: "percussive" },
        { id: "d", ipa: "d", display: "d", kind: "percussive" },
        { id: "n", ipa: "n", display: "n", kind: "sustained", minHoldMs: 70 },
    ],
    words: [
        {
            id: "rabbit",
            text: "rabbit",
            ipa: "ˈɹæb.ɪt",
            difficulty: 2,
            prompt: { text: "Say: rabbit", icon: "🐰", carrierPhrase: "I see a ___." },
            sequence: [
                { phone: "r", role: "target" },
                { phone: "ae", role: "scaffold" },
                { phone: "b", role: "scaffold" },
                { phone: "ih", role: "optional" },
                { phone: "t", role: "scaffold" },
            ],
            expectedDurationMs: { min: 320, max: 1500 },
            notes: "Watch for /w/ substitution on the onset.",
        },
        {
            id: "rain",
            text: "rain",
            ipa: "ɹeɪn",
            difficulty: 1,
            prompt: { text: "Say: rain", icon: "🌧️", carrierPhrase: "I like the ___." },
            sequence: [
                { phone: "r", role: "target" },
                { phone: "ey", role: "scaffold" },
                { phone: "n", role: "scaffold" },
            ],
            expectedDurationMs: { min: 250, max: 1400 },
        },
        {
            id: "red",
            text: "red",
            ipa: "ɹɛd",
            difficulty: 1,
            prompt: { text: "Say: red", icon: "🟥", carrierPhrase: "The ball is ___." },
            sequence: [
                { phone: "r", role: "target" },
                { phone: "eh", role: "scaffold" },
                { phone: "d", role: "scaffold" },
            ],
            expectedDurationMs: { min: 220, max: 1300 },
        },
    ],
    drills: [
        {
            id: "warmup",
            title: "Warm-up — 5 each",
            wordIds: ["rabbit", "rain", "red"],
            repetitions: 5,
            order: "sequential",
            criterion: { correctOf: 4, outOf: 5 },
        },
        {
            id: "rabbit-10",
            title: "Rabbit ×10",
            wordIds: ["rabbit"],
            repetitions: 10,
            order: "sequential",
        },
    ],
};
export const DEFAULT_LANES_WORD_SET = {
    schemaVersion: 1,
    id: "en-us.default-lanes",
    title: "Default sounds — sh / f / p / m words",
    description: "Words built from the four shipped sounds so alignment can anchor on " +
        "calibrated clusters. Vowels are provisional.",
    locale: "en-US",
    library: "default",
    phones: [
        { id: "sh", ipa: "ʃ", display: "sh", clusterId: "hiss", kind: "sustained", minHoldMs: 140 },
        { id: "f", ipa: "f", display: "f", clusterId: "airy", kind: "sustained", minHoldMs: 120 },
        { id: "p", ipa: "p", display: "p", clusterId: "pop", kind: "percussive" },
        { id: "m", ipa: "m", display: "m", clusterId: "hum", kind: "sustained", minHoldMs: 110 },
        { id: "ih", ipa: "ɪ", display: "i", kind: "sustained", minHoldMs: 60 },
        { id: "ae", ipa: "æ", display: "a", kind: "sustained", minHoldMs: 90 },
        { id: "uh", ipa: "ʌ", display: "u", kind: "sustained", minHoldMs: 70 },
    ],
    words: [
        {
            id: "ship",
            text: "ship",
            ipa: "ʃɪp",
            prompt: { text: "Say: ship", icon: "🚢", carrierPhrase: "Here comes the ___." },
            sequence: [
                { phone: "sh", role: "target" },
                { phone: "ih", role: "optional" },
                { phone: "p", role: "scaffold" },
            ],
            expectedDurationMs: { min: 220, max: 1400 },
        },
        {
            id: "fish",
            text: "fish",
            ipa: "fɪʃ",
            prompt: { text: "Say: fish", icon: "🐟", carrierPhrase: "Look, a ___." },
            sequence: [
                { phone: "f", role: "target" },
                { phone: "ih", role: "optional" },
                { phone: "sh", role: "scaffold" },
            ],
            expectedDurationMs: { min: 220, max: 1400 },
        },
        {
            id: "map",
            text: "map",
            ipa: "mæp",
            prompt: { text: "Say: map", icon: "🗺️", carrierPhrase: "Read the ___." },
            sequence: [
                { phone: "m", role: "scaffold" },
                { phone: "ae", role: "optional" },
                { phone: "p", role: "target" },
            ],
            expectedDurationMs: { min: 200, max: 1300 },
        },
        {
            id: "puff",
            text: "puff",
            ipa: "pʌf",
            prompt: { text: "Say: puff", icon: "💨", carrierPhrase: "Give it a ___." },
            sequence: [
                { phone: "p", role: "scaffold" },
                { phone: "uh", role: "optional" },
                { phone: "f", role: "target" },
            ],
            expectedDurationMs: { min: 200, max: 1300 },
        },
    ],
    drills: [
        {
            id: "all-5",
            title: "All words — 5 each",
            wordIds: ["ship", "fish", "map", "puff"],
            repetitions: 5,
            order: "sequential",
        },
    ],
};
export const BUILTIN_WORD_SETS = [
    DEFAULT_LANES_WORD_SET,
    INITIAL_R_WORD_SET,
];
